import { useMemo, useState } from "react";
import { Eye, EyeOff, Search } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { FilterProduct } from "./bundle-grouped-filter-bar";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: FilterProduct[];
  // Selected product ids = the "show only these" include-set (empty = show all).
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
};

export default function ProductVisibilityPanel({
  open,
  onOpenChange,
  products,
  selectedIds,
  onToggle,
  onClear,
}: Props) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle
      ? products.filter((p) => p.name.toLowerCase().includes(needle))
      : products;
  }, [products, q]);

  const onDashboardCount = products.filter(
    (p) => p.on_dashboard === true,
  ).length;
  const hasSignal = products.some((p) => p.on_dashboard != null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[380px] sm:w-[420px] p-0 flex flex-col">
        <SheetHeader className="px-5 pt-5 pb-3 border-b">
          <SheetTitle>Product visibility</SheetTitle>
          <SheetDescription>
            {hasSignal
              ? `${onDashboardCount} of ${products.length} products are live on the patient dashboard in this region. Tick products to show only those in the list.`
              : "Pick a specific region to see which products are live on the dashboard. Tick products to show only those in the list."}
          </SheetDescription>
        </SheetHeader>

        <div className="px-5 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products…"
              className="h-8 pl-8 text-xs"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">
              {selectedIds.length > 0
                ? `Showing ${selectedIds.length} selected`
                : "Showing all products"}
            </span>
            {selectedIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px] text-[#EA5B34]"
                onClick={onClear}
              >
                Show all
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-gray-400">
                No products match.
              </p>
            ) : (
              filtered.map((p) => {
                const checked = selectedIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onToggle(p.id)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-gray-50"
                  >
                    <Checkbox
                      checked={checked}
                      className="pointer-events-none shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-gray-800">{p.name}</p>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400">
                        {p.status}
                      </p>
                    </div>
                    {p.on_dashboard === true ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
                        <Eye className="h-3 w-3" /> On dashboard
                      </span>
                    ) : p.on_dashboard === false ? (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                        <EyeOff className="h-3 w-3" /> Hidden
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
